
import { Component, EventEmitter, Input, Output } from '@angular/core';

import { DialogModule } from 'primeng/dialog';
import { IDialogSettings } from '../../interfaces/dialog.interface';

@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [DialogModule],
  templateUrl: './dialog.component.html',
})
export class DialogComponent {
  // Core Dialog Properties
  @Input() public isVisible: boolean = false;
  @Input() public dialogHeader: string = '';
  /** Settings object for configuring the dialog. */
  @Input() public settings: IDialogSettings = {
    showCustomHeader: false,
    modal: true,
    position: 'center',
    isMaximizable: false,
    breakpoints: {},
    isDraggable: false,
    isResizable: true,
    closeOnEscape: true,
    showCustomFooter: false,
    width: '100%',
    maxWidth: '400px',
  };
  @Input() public customClass?: string;

  // Dialog Events Configuration
  @Input() public emitOnShow: boolean = false;
  @Input() public emitOnHide: boolean = false;
  @Input() public emitOnDragEnd: boolean = false;
  @Input() public emitOnMaximize: boolean = false;

  // Dialog Interaction Properties
  @Output() public onShow: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onHide: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onDragEnd: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onMaximize: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Emits the provided event if enabled.
   * @param event The event emitter to emit.
   * @param enabled Flag indicating whether the event emission is enabled.
   */
  private emitEvent(event: EventEmitter<void>, enabled: boolean): void {
    if (enabled) {
      event.emit();
    }
  }

  /**
   * Handles the 'onShow' event of the dialog.
   * Emits the 'onShow' event if enabled in settings.
   * @remarks This method is triggered when the dialog is shown.
   */
  public handleOnShow(): void {
    this.emitEvent(this.onShow, this.emitOnShow ?? false);
  }

  /**
   * Handles the 'onHide' event of the dialog.
   * Emits the 'onHide' event if enabled in settings.
   * @remarks This method is triggered when the dialog is hidden.
   */
  public handleOnHide(): void {
    this.emitEvent(this.onHide, this.emitOnHide ?? false);
  }

  /**
   * Handles the 'onDragEnd' event of the dialog.
   * Emits the 'onDragEnd' event if enabled in settings.
   * @remarks This method is triggered when dragging of the dialog ends.
   */
  public handleOnDragEnd(): void {
    if (this.emitOnDragEnd) {
      this.emitEvent(this.onDragEnd, this.emitOnDragEnd ?? false);
    }
  }

  /**
   * Handles the 'onMaximize' event of the dialog.
   * Emits the 'onMaximize' event if enabled in settings.
   * @remarks This method is triggered when the dialog is maximized.
   */
  public handleOnMaximize(): void {
    this.emitEvent(this.onMaximize, this.emitOnMaximize ?? false);
  }
}
